/**
 * useConferenceInit: 마스터 측 입장 대기자 감지 훅.
 *
 * v1 원본: RenewalComponents/ConferenceInit/ConferenceInitContainer.js
 * v2 변환: 커스텀 훅 (렌더링 없음, 부수효과만)
 *
 * 역할:
 * - security_type === 'MASTER_APPROVAL'인 비공개방에서만 동작
 * - lpevent 롱폴링으로 참여 요청(join_request) 수신
 * - getRequestJoinUsers()로 대기 사용자 목록 조회
 * - 수신한 요청은 masterSlice에 저장 + notificationSlice에 카드 추가
 */
import { useEffect, useRef, useCallback } from 'react';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { masterActions, type RequestJoinUser } from '@/store/slices/masterSlice';
import { notificationActions } from '@/store/slices/notificationSlice';
import apiManager from '@/api';
import { store } from '@/store';
import { getUserIdentify } from '@/utils/userIdentificationCode';
import { showNotification, dismissNotification as dismissGlobalNotification } from '@/utils/globalToast';

/**
 * 마스터 측 입장 대기자 롱폴링 훅.
 * 비공개 + security_type=MASTER_APPROVAL인 방에서만 활성화.
 *
 * ref 기반 구현: lpeventHandler ↔ startLpevent 순환 호출 시
 * stale closure 문제를 방지하기 위해 모든 상태를 ref 또는 store.getState()로 참조.
 */
export function useConferenceInit() {
  const dispatch = useAppDispatch();
  const local = useAppSelector((s) => s.local);
  const masterUsers = useAppSelector((s) => s.master.masterUsers);
  const isMountedRef = useRef(true);
  const hasStartedRef = useRef(false);
  const lpeventAbortRef = useRef<AbortController | null>(null);

  const isMaster = masterUsers.some(
    (m) => m.user === local.userId || m.user === local.wehagoId,
  );
  const isPrivateMasterRoom =
    !local.isPublic && local.securityType === 'MASTER_APPROVAL' && isMaster;

  // ─── ref로 최신 함수 유지 (stale closure 방지) ──────────────────────
  const addJoinUser = (user: RequestJoinUser) => {
    dispatch(masterActions.addRequestJoinUser(user));
    dispatch(
      notificationActions.addCard({
        type: 'REQUEST_INVITE',
        userId: user.user_identify,
        userName: user.user_name,
        profileUrl: user.profile_url,
      }),
    );

    // 상단 플로팅 알림 표시
    const notifId = `join_request_${user.user_identify}`;
    showNotification({
      id: notifId,
      highlight: user.user_name || '알 수 없는 사용자',
      text: '님이 참석을 요청하였습니다.',
      buttons: [
        {
          label: '거절',
          onClick: () => {
            respondJoinRequest(user.user_identify, 'deny');
          },
        },
        {
          label: '승인',
          variant: 'blue',
          onClick: () => {
            respondJoinRequest(user.user_identify, 'allow');
          },
        },
      ],
    });
  };

  /** 참여 요청 응답 (플로팅 알림 / 사이드바 공용) */
  const respondJoinRequest = async (userIdentify: string, status: 'allow' | 'deny') => {
    const token = store.getState().local.token;
    if (!token) return;
    try {
      await apiManager.responseJoinRoom({ targetUsers: [{ user_identify: userIdentify }], status, token });
      dispatch(masterActions.removeRequestJoinUser([{ user_identify: userIdentify }]));
      dispatch(notificationActions.removeCardsByUserId({ type: 'REQUEST_INVITE', userId: userIdentify }));
      dismissGlobalNotification(`join_request_${userIdentify}`);
    } catch (err) {
      console.warn('[useConferenceInit] responseJoinRoom 실패:', err);
    }
  };

  // respondJoinRequest를 외부에서 접근 가능하도록 ref로 보관
  const respondJoinRequestRef = useRef(respondJoinRequest);
  respondJoinRequestRef.current = respondJoinRequest;

  const startLpeventRef = useRef<() => void>(() => {});

  const lpeventHandler = (data: {
    extra_data: {
      type: string;
      user_identify?: string;
      user_name?: string;
      profile_url?: string;
      removeUserIdentifyList?: string[];
    };
  }) => {
    if (!isMountedRef.current) return;

    const { type, user_identify, user_name, profile_url, removeUserIdentifyList } =
      data.extra_data;

    if (type === 'join_request' && user_identify) {
      addJoinUser({
        user_identify,
        user_name: user_name ?? '',
        profile_url,
        hasViewed: false,
      });
    }

    if (type === 'master_join_response' && removeUserIdentifyList) {
      dispatch(
        masterActions.removeRequestJoinUser(
          removeUserIdentifyList.map((id) => ({ user_identify: id })),
        ),
      );
      removeUserIdentifyList.forEach((id) => {
        dispatch(
          notificationActions.removeCardsByUserId({ type: 'REQUEST_INVITE', userId: id }),
        );
      });
    }

    // 다시 롱폴링 — ref를 통해 항상 최신 함수 호출
    startLpeventRef.current();
  };

  const lpeventHandlerRef = useRef(lpeventHandler);
  lpeventHandlerRef.current = lpeventHandler;

  const startLpevent = () => {
    if (!isMountedRef.current) return;
    const s = store.getState().local;
    apiManager.lpevent(
      {
        room: s.roomId,
        userIdentify: s.userIdentify || getUserIdentify(),
        profileUrl: s.profileUrl,
        timeout: 30,
        jwt: s.token || undefined,
      },
      (data) => lpeventHandlerRef.current(data),
      lpeventAbortRef.current?.signal,
    );
  };
  startLpeventRef.current = startLpevent;

  const fetchRequestJoinUsers = async () => {
    const token = store.getState().local.token;
    if (!token) return;
    try {
      const users = await apiManager.getRequestJoinUsers(token);
      users.forEach((u) => {
        addJoinUser({ ...u, hasViewed: false });
      });
    } catch {
      // pass
    }
  };

  // ─── 초기화 / 정리 ────────────────────────────────────────────────────
  useEffect(() => {
    if (!isPrivateMasterRoom || hasStartedRef.current) return;

    isMountedRef.current = true;
    hasStartedRef.current = true;
    lpeventAbortRef.current = new AbortController();

    fetchRequestJoinUsers();
    startLpevent();

    return () => {
      isMountedRef.current = false;
      hasStartedRef.current = false;
      lpeventAbortRef.current?.abort();
      lpeventAbortRef.current = null;
    };
  }, [isPrivateMasterRoom]); // eslint-disable-line react-hooks/exhaustive-deps

  /** 외부(사이드바 등)에서 참여 요청 응답 시 호출 — 플로팅 알림도 함께 정리 */
  const handleResponseJoinRequest = useCallback(
    async (userId: string, status: 'allow' | 'deny') => {
      await respondJoinRequestRef.current(userId, status);
    },
    [],
  );

  return { handleResponseJoinRequest };
}
